// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Badge,
  SimpleGrid,
  ThemeIcon,
  Loader,
  Center,
  Textarea,
  Select,
  Alert,
} from '@mantine/core';
import {
  IconMapPin,
  IconClock,
  IconLogin,
  IconLogout,
  IconAlertTriangle,
  IconCheck,
  IconCurrentLocation,
  IconCoffee,
} from '@tabler/icons-react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { PageHeader } from '../../components/PageHeader';
import { useGpsPunchIn, useGpsPunchOut, useEnhancedPunches } from '../../hooks/useGpsPunch';
import type { EnhancedPunch } from '../../types/gpsPunch';

export default function GpsPunchPage() {
  const [note, setNote] = useState('');
  const [locating, setLocating] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number; acc: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [punchType, setPunchType] = useState<string>('IN');

  const punchIn = useGpsPunchIn();
  const punchOut = useGpsPunchOut();
  const { data: recentPunchesRaw, loading: isLoading } = useEnhancedPunches({
    startDate: new Date().toISOString().split('T')[0],
  });
  const recentPunches = recentPunchesRaw as unknown as EnhancedPunch[] | undefined;

  const getLocation = useCallback(() => {
    setLocating(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setLocating(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy,
        });
        setLocating(false);
      },
      (err) => {
        setLocationError(
          err.code === 1
            ? 'Location access denied. Please enable location permissions.'
            : err.code === 2
              ? 'Location unavailable. Please try again.'
              : 'Location request timed out. Please try again.',
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }, []);

  const handlePunch = useCallback(() => {
    if (!location) {
      notifications.show({
        title: 'Location Required',
        message: 'Please get your location first before punching.',
        color: 'orange',
      });
      return;
    }

    const input = {
      latitude: location.lat,
      longitude: location.lng,
      accuracy: location.acc,
      note: note.trim() || undefined,
      source: 'WEB',
    };

    const mutation = punchType === 'IN' || punchType === 'BREAK_END' ? punchIn : punchOut;

    mutation.mutate(input, {
      onSuccess: (data: any) => {
        const result = 'gpsPunchIn' in data ? data.gpsPunchIn : data.gpsPunchOut;
        const insideFence = result.gps?.insideFence;

        notifications.show({
          title: `Punch ${punchType === 'IN' ? 'In' : punchType === 'OUT' ? 'Out' : punchType === 'BREAK_START' ? 'Break Start' : 'Break End'} Recorded`,
          message: insideFence === false
            ? 'Warning: You are outside the assigned geofence.'
            : 'Your punch has been recorded successfully.',
          color: insideFence === false ? 'orange' : 'green',
        });
        setNote('');
      },
      onError: (err: any) => {
        notifications.show({
          title: 'Punch Failed',
          message: err instanceof Error ? err.message : 'Failed to record punch',
          color: 'red',
        });
      },
    });
  }, [location, note, punchType, punchIn, punchOut]);

  const todayPunches = recentPunches || [];

  return (
    <Stack gap="md">
      <PageHeader
        title="GPS Punch Clock"
        subtitle="Clock in and out with location verification"
        breadcrumbs={[
          { label: 'Time & Attendance', to: '/admin/workforce/time' },
          { label: 'GPS Punch' },
        ]}
      />

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Punch Card */}
        <Paper p="xl" withBorder radius="md">
          <Stack gap="lg">
            <Group justify="center">
              <ThemeIcon size={64} radius="xl" variant="light" color="blue">
                <IconClock size={32} />
              </ThemeIcon>
            </Group>

            <Text ta="center" size="xl" fw={700}>
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
            <Text ta="center" size="sm" c="dimmed">
              {new Date().toLocaleDateString([], {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Text>

            {/* Location Section */}
            {location ? (
              <Alert icon={<IconCheck size={16} />} color="green" title="Location Acquired">
                <Text size="xs">
                  Lat: {location.lat.toFixed(6)}, Lng: {location.lng.toFixed(6)}
                </Text>
                <Text size="xs" c="dimmed">
                  Accuracy: {location.acc.toFixed(0)}m
                </Text>
              </Alert>
            ) : locationError ? (
              <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Location Error">
                {locationError}
              </Alert>
            ) : null}

            <Button
              variant="light"
              icon={
                locating ? <Loader size={16} /> : <IconCurrentLocation size={16} />
              }
              onClick={getLocation}
              loading={locating}
              fullWidth
            >
              {location ? 'Refresh Location' : 'Get Location'}
            </Button>

            {/* Punch Type */}
            <Select
              label="Punch Type"
              value={punchType}
              onChange={(v) => setPunchType(v || 'IN')}
              data={[
                { value: 'IN', label: 'Punch In' },
                { value: 'OUT', label: 'Punch Out' },
                { value: 'BREAK_START', label: 'Start Break' },
                { value: 'BREAK_END', label: 'End Break' },
              ]}
            />

            <Textarea
              label="Note (optional)"
              placeholder="Add a note for this punch..."
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              minRows={2}
            />

            <Button
              size="lg"
              fullWidth
              color={punchType === 'OUT' || punchType === 'BREAK_START' ? 'red' : 'green'}
              icon={
                punchType === 'IN' || punchType === 'BREAK_END' ? (
                  <IconLogin size={20} />
                ) : punchType === 'BREAK_START' ? (
                  <IconCoffee size={20} />
                ) : (
                  <IconLogout size={20} />
                )
              }
              onClick={handlePunch}
              loading={punchIn.isPending || punchOut.isPending}
              disabled={!location}
            >
              {punchType === 'IN'
                ? 'Punch In'
                : punchType === 'OUT'
                  ? 'Punch Out'
                  : punchType === 'BREAK_START'
                    ? 'Start Break'
                    : 'End Break'}
            </Button>
          </Stack>
        </Paper>

        {/* Today's Punches */}
        <Paper p="xl" withBorder radius="md">
          <Text size="lg" fw={600} mb="md">
            Today&apos;s Punches
          </Text>

          {isLoading ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : todayPunches.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No punches recorded today
            </Text>
          ) : (
            <Stack gap="sm">
              {todayPunches.map((punch) => (
                <Paper key={punch.id} p="sm" withBorder radius="sm">
                  <Group justify="space-between">
                    <Group gap="sm">
                      <ThemeIcon
                        size="sm"
                        variant="light"
                        color={
                          punch.type === 'IN' || punch.type === 'BREAK_END'
                            ? 'green'
                            : 'red'
                        }
                      >
                        {punch.type === 'IN' || punch.type === 'BREAK_END' ? (
                          <IconLogin size={12} />
                        ) : punch.type === 'BREAK_START' ? (
                          <IconCoffee size={12} />
                        ) : (
                          <IconLogout size={12} />
                        )}
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={500}>
                          {punch.type === 'IN'
                            ? 'Punch In'
                            : punch.type === 'OUT'
                              ? 'Punch Out'
                              : punch.type === 'BREAK_START'
                                ? 'Break Start'
                                : 'Break End'}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(punch.timestamp).toLocaleTimeString()}
                        </Text>
                      </div>
                    </Group>
                    <Group gap="xs">
                      {punch.gps && (
                        <Badge
                          size="xs"
                          variant="light"
                          color={punch.gps.insideFence ? 'green' : 'orange'}
                          icon={<IconMapPin size={10} />}
                        >
                          {punch.gps.insideFence ? 'In Fence' : 'Outside'}
                        </Badge>
                      )}
                      <Badge
                        size="xs"
                        variant="light"
                        color={
                          punch.status === 'VALID'
                            ? 'green'
                            : punch.status === 'FLAGGED'
                              ? 'orange'
                              : 'gray'
                        }
                      >
                        {punch.status}
                      </Badge>
                      <Badge size="xs" variant="light" color="gray">
                        {punch.source}
                      </Badge>
                    </Group>
                  </Group>
                  {punch.note && (
                    <Text size="xs" c="dimmed" mt={4}>
                      {punch.note}
                    </Text>
                  )}
                  {punch.flagReason && (
                    <Text size="xs" c="orange" mt={4}>
                      Flag: {punch.flagReason}
                    </Text>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}
